<script setup>
import PanelHandWrite from '@/components/common/CustomKeyboard/PanelHandWrite.vue'
defineOptions({
  name: 'Keyboard'
})

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },

  /**
   * 支持的语言
   * [ 'zh', 'en'] // 支持中文和英文
   * ['en'] // 仅支持英文
   * ['zh'] // 仅支持中文
   */
  enableLanguages: {
    type: Array,
    default: () => ['en']
  },

  /**
   * 键盘类型
   * [ 'en', 'num'] // 支持英文和数字键盘
   * ['en'] // 仅英文键盘
   * ['num'] // 仅数字键盘
   */
  types: {
    type: Array,
    default: () => ['en', 'num']
  },

  /**
   * 数字键盘的布局方式
   * horizontal 水平布局
   * vertical 垂直布局
   */
  numberLayout: {
    type: String,
    default: 'horizontal'
  }
})

const emits = defineEmits(['change', 'click-keyboard', 'update:modelValue'])
const inputValue = ref('') // 输入的字符串
watch(
  () => props.modelValue,
  (val) => {
    if (val + '' === inputValue.value) {
      return
    }
    inputValue.value = val + ''
  },
  { immediate: true }
)

watch(inputValue, (val) => {
  emits('update:modelValue', val)
})
/**
 * 输入值
 * @param val
 */
function inputVal(val) {
  inputValue.value += val
  emits('change', inputValue.value)
  emits('click-keyboard', val)
}
/**
 * 回退
 */
function removeBack() {
  emits('click-keyboard', 'back')
  if (inputValue.value === '') {
    return
  }
  inputValue.value = inputValue.value.slice(0, -1)
  emits('change', inputValue.value)
}

/**
 * 选择中文字符
 * @param character
 */
function handleSelectCharacter(character) {
  inputValue.value += character
  emits('change', inputValue.value)
}

const languageTabs = computed(() => {
  const tabs = [
    { label: '中文', value: 'zh' },
    { label: '英文', value: 'en' }
  ]
  return tabs.filter((tab) => props.enableLanguages.includes(tab.value))
})
const currentLanguage = ref('en')
function initCurrentLanguage() {
  currentLanguage.value = props.enableLanguages?.[0] || 'en'
}
function changeLanguage(lang) {
  currentLanguage.value = lang
}
onMounted(() => {
  initCurrentLanguage()
})
</script>

<template>
  <div class="custom-keyboard">
    <div v-if="languageTabs.length > 1" class="tabs">
      <div
        v-for="tab in languageTabs"
        :key="tab.value"
        :class="['tab-item', { active: currentLanguage === tab.value }]"
        @click="changeLanguage(tab.value)"
      >
        {{ tab.label }}
      </div>
    </div>
    <!-- 中文输入面板 -->
    <PanelHandWrite
      v-if="currentLanguage === 'zh'"
      @select-character="handleSelectCharacter"
      @remove-back="removeBack"
    />
    <!-- 英文输入面板 -->
    <div
      v-if="currentLanguage === 'en'"
      class="custom-keyboard-en"
      :class="{ 'number-layout-vertical': props.numberLayout === 'vertical' }"
    >
      <div
        v-if="props.types.includes('num') && props.numberLayout === 'vertical'"
        class="row-number"
      >
        <div
          v-for="key in ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']"
          :key="key"
          class="key-item"
          @click="inputVal(key)"
        >
          {{ key }}
        </div>
        <div key="back" class="key-item key-back" @click="removeBack">回退</div>
      </div>

      <div v-if="props.types.includes('en')" class="row-number-container">
        <div class="row-number">
          <div
            v-for="key in ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P']"
            :key="key"
            class="key-item"
            @click="inputVal(key)"
          >
            {{ key }}
          </div>
        </div>
        <div class="row-number ml-50">
          <div
            v-for="key in ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L']"
            :key="key"
            class="key-item"
            @click="inputVal(key)"
          >
            {{ key }}
          </div>
        </div>
        <div class="row-number" style="margin-left: 100px; margin-right: 50px">
          <div
            v-for="key in ['Z', 'X', 'C', 'V', 'B', 'N', 'M']"
            :key="key"
            class="key-item"
            @click="inputVal(key)"
          >
            {{ key }}
          </div>
        </div>
      </div>
      <div
        v-if="props.types.includes('num') && props.numberLayout === 'horizontal'"
        class="panel-option"
      >
        <div class="row-number">
          <div
            v-for="key in ['1', '2', '3', '4']"
            :key="key"
            class="key-item"
            @click="inputVal(key)"
          >
            {{ key }}
          </div>
        </div>
        <div class="row-number">
          <div
            v-for="key in ['5', '6', '7', '8']"
            :key="key"
            class="key-item"
            @click="inputVal(key)"
          >
            {{ key }}
          </div>
        </div>
        <div class="row-number">
          <div v-for="key in ['9', '0']" :key="key" class="key-item" @click="inputVal(key)">
            {{ key }}
          </div>
          <div key="back" class="key-item key-back" @click="removeBack">回退</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/mixin.scss';
.tabs {
  display: flex;
  margin-bottom: 15px;

  .tab-item {
    padding: 0 10px;
    height: 32px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: $radius;
    cursor: pointer;
    min-width: 76px;
    text-align: center;
    line-height: 32px;

    &.active {
      @include background-theme-gradient;
    }

    & + .tab-item {
      margin-left: 10px;
    }
  }
}
.custom-keyboard-en {
  display: inline-flex;

  &.number-layout-vertical {
    display: inline;

    .row-number-container {
      margin-left: 50px;
      margin-right: 50px;
    }
  }

  .panel-option {
    flex: 3;
  }

  .row-number {
    display: flex;
    flex-direction: row;
  }

  .key-item {
    flex: 1;
    text-align: center;
    border: 1px solid rgba(255, 255, 255, 0.25);
    font-size: 18px;
    min-width: 40px;
    height: 40px;
    flex-shrink: 0;
    margin: 5px;
    background-color: rgb(15, 12, 58);
    border-radius: $radius;
    display: flex;
    align-items: center;
    justify-content: center;

    &.key-back {
      flex-grow: 2;
      flex-shrink: 0;
      min-width: 90px;
    }
  }

  .key-item:active {
    background: #eee;
    color: #333333;
  }

  .panel-operation {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: #ff5600;
  }

  .panel-operation.invalid {
    background: rgba(255, 86, 0, 0.58);
  }

  .panel-operation.active:active {
    background: #e64e00;
  }

  .btn-operation {
    font-size: 16px;
    color: white;
  }

  .vertical-text {
    -webkit-writing-mode: vertical-rl;
    writing-mode: vertical-rl;
    line-height: 1.7em;
    letter-spacing: 0.8em;
  }
}
</style>
